import { keyframeProgress, type KeyframeEasing } from './keyframe-easing';

/**
 * 줌 키프레임 — "이 시점(아이템 로컬 프레임)에 이 영역(bbox)을 확대해서 보여줘".
 *
 * croppable 아이템(video/image/gif/webp)의 `zoomKeyframes` 필드에 저장되는 1급
 * 데이터다(positionKeyframes 와 같은 패턴). 아이템 프레임은 고정하고 내부 콘텐츠만
 * 확대·이동한다(punch-in). 렌더는 croppable layer 가 outer(overflow hidden) 안에
 * zoom wrapper 를 끼워 현재 프레임의 보간 rect 를 scale+translate 로 합성한다.
 *
 * rect 는 보이는 창(post-crop) 기준 fraction:
 * - left/top ∈ [0, 1-size], size ∈ (0, 1]. fraction 공간에서 w==h 라 창 비율이
 *   자동 유지된다(배율 = 1/size).
 * - identity(줌 없음) = { left: 0, top: 0, size: 1 }.
 * - fraction 이라 아이템 리사이즈·export 해상도 스케일링에 불변.
 */
export interface ZoomKeyframe {
  /** 아이템 로컬 프레임 */
  frame: number;
  /** 이 키프레임에서 다음까지의 곡선. 생략하면 linear. */
  easing?: KeyframeEasing;
  /** 뷰 창 좌상단 (보이는 창 기준 fraction) */
  left: number;
  top: number;
  /** 뷰 창 크기 fraction (0,1] — 배율 = 1/size */
  size: number;
}

export interface ZoomRect {
  left: number;
  top: number;
  size: number;
}

export const IDENTITY_ZOOM_RECT: ZoomRect = { left: 0, top: 0, size: 1 };

/** 최소 뷰 창 크기(최대 배율 10x) — 핸들 드래그·수치 입력 공통 클램프. */
export const MIN_ZOOM_SIZE = 0.1;

/** left/top/size 를 유효 범위로 클램프한 rect. */
export const clampZoomRect = (rect: ZoomRect): ZoomRect => {
  const size = Math.min(1, Math.max(MIN_ZOOM_SIZE, rect.size));
  return {
    size,
    left: Math.min(1 - size, Math.max(0, rect.left)),
    top: Math.min(1 - size, Math.max(0, rect.top)),
  };
};

/** 같은 프레임(반올림 기준)이면 교체, 아니면 삽입한 새 목록(프레임 오름차순). */
export const upsertZoomKeyframe = (
  keyframes: ZoomKeyframe[],
  keyframe: ZoomKeyframe,
): ZoomKeyframe[] => {
  const frame = Math.round(keyframe.frame);
  const previous = keyframes.find((k) => Math.round(k.frame) === frame);
  const easing = keyframe.easing === undefined ? previous?.easing : keyframe.easing;
  if (easing !== undefined) keyframeProgress(0, easing);
  const next = keyframes.filter((k) => Math.round(k.frame) !== frame);
  next.push({ ...clampZoomRect(keyframe), frame, ...(easing === undefined ? {} : { easing }) });
  return next.sort((a, b) => a.frame - b.frame);
};

/**
 * 키프레임 곡선의 특정 프레임 rect.
 *
 * - 첫 키프레임 이전: identity(전체 보기) — "전체를 보여줬다가 그 시점부터 줌"이
 *   키프레임 1개로 성립한다(punch-in 컷). 부드러운 줌인은 앞에 키프레임을 하나 더
 *   추가하면 된다(첫 키프레임 이전 시점의 ◆ 추가는 identity 를 기록하므로 자연 조합).
 * - 마지막 키프레임 이후: 마지막 값 유지.
 * - 구간 내: 앞 키프레임의 easing 보간(생략하면 선형).
 */
export const getZoomRectAt = (
  keyframes: ZoomKeyframe[],
  frame: number,
): ZoomRect => {
  if (keyframes.length === 0) return IDENTITY_ZOOM_RECT;
  const sorted = [...keyframes].sort((a, b) => a.frame - b.frame);
  const first = sorted[0] as ZoomKeyframe;
  const last = sorted[sorted.length - 1] as ZoomKeyframe;
  // 반환은 frame 없는 순수 rect — 키프레임 객체를 그대로 반환하면 caller 의
  // `{ frame, ...rect }` 합성에서 frame 이 덮여 upsert 가 기존 키프레임을
  // 자기 자신으로 교체한다(추가 무반응 사고, 2026-07-18).
  const toRect = ({ left, top, size }: ZoomKeyframe): ZoomRect => ({
    left,
    top,
    size,
  });
  if (frame < first.frame) return IDENTITY_ZOOM_RECT;
  if (frame === first.frame) return toRect(first);
  if (frame >= last.frame) return toRect(last);
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i] as ZoomKeyframe;
    const b = sorted[i + 1] as ZoomKeyframe;
    if (frame >= a.frame && frame <= b.frame) {
      if (frame === b.frame) return toRect(b);
      const t = keyframeProgress(
        b.frame === a.frame ? 0 : (frame - a.frame) / (b.frame - a.frame),
        a.easing,
      );
      return {
        left: a.left + (b.left - a.left) * t,
        top: a.top + (b.top - a.top) * t,
        size: a.size + (b.size - a.size) * t,
      };
    }
  }
  return toRect(last);
};

/** 반올림 프레임 기준으로 그 프레임에 키프레임이 있는지. */
export const hasZoomKeyframeAt = (
  keyframes: ZoomKeyframe[],
  frame: number,
): boolean => {
  const target = Math.round(frame);
  return keyframes.some((k) => Math.round(k.frame) === target);
};

/** 제거 후 앞 키프레임의 outgoing 곡선은 새 다음 키프레임까지 적용된다. */
export const removeZoomKeyframeAt = (
  keyframes: ZoomKeyframe[],
  frame: number,
): ZoomKeyframe[] => {
  const target = Math.round(frame);
  return keyframes.filter((k) => Math.round(k.frame) !== target);
};

/** 현재 프레임(반올림)보다 앞선 가장 가까운 키프레임. 없으면 undefined. */
export const findPrevZoomKeyframe = (
  keyframes: ZoomKeyframe[],
  frame: number,
): ZoomKeyframe | undefined => {
  const target = Math.round(frame);
  const before = keyframes
    .filter((k) => Math.round(k.frame) < target)
    .sort((a, b) => a.frame - b.frame);
  return before[before.length - 1];
};

/** 현재 프레임(반올림)보다 뒤인 가장 가까운 키프레임. 없으면 undefined. */
export const findNextZoomKeyframe = (
  keyframes: ZoomKeyframe[],
  frame: number,
): ZoomKeyframe | undefined => {
  const target = Math.round(frame);
  const after = keyframes
    .filter((k) => Math.round(k.frame) > target)
    .sort((a, b) => a.frame - b.frame);
  return after[0];
};

export type ZoomRegionCorner =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

/**
 * 코너 드래그 리사이즈(비율 고정) — 잡은 코너의 반대 코너를 고정하고, 지배 축
 * (delta 절대값이 큰 축)으로 size 를 결정한다. delta 는 fraction 단위.
 */
export const resizeZoomRectFromCorner = (
  rect: ZoomRect,
  corner: ZoomRegionCorner,
  deltaX: number,
  deltaY: number,
): ZoomRect => {
  const signX = corner.includes('right') ? 1 : -1;
  const signY = corner.includes('bottom') ? 1 : -1;
  const candidate =
    Math.abs(deltaX) >= Math.abs(deltaY)
      ? rect.size + signX * deltaX
      : rect.size + signY * deltaY;

  // 고정 코너가 [0,1] 밖으로 나가지 않는 최대 크기
  const maxFromX = corner.includes('left')
    ? rect.left + rect.size
    : 1 - rect.left;
  const maxFromY = corner.includes('top') ? rect.top + rect.size : 1 - rect.top;
  const size = Math.min(maxFromX, maxFromY, Math.max(MIN_ZOOM_SIZE, candidate));

  return {
    size,
    left: corner.includes('left') ? rect.left + rect.size - size : rect.left,
    top: corner.includes('top') ? rect.top + rect.size - size : rect.top,
  };
};

/**
 * zoom wrapper 스타일 — outer(보이는 창, overflow hidden) 바로 아래에 끼우는 div.
 * translate 의 % 는 wrapper 자기 크기(=창 크기) 기준이고 CSS transform 은 오른쪽부터
 * 적용되므로 p' = (1/size)·(p − left·W) → 뷰 rect 가 창을 정확히 채운다.
 */
export const getZoomWrapperStyle = (rect: ZoomRect): React.CSSProperties => {
  const scale = 1 / rect.size;
  return {
    position: 'absolute',
    inset: 0,
    transformOrigin: '0 0',
    transform: `scale(${scale}) translate(${-rect.left * 100}%, ${-rect.top * 100}%)`,
  };
};
